import { useRef, useState, useEffect } from 'react';
import * as THREE from 'three/webgpu';
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js';

import {
  abs,
  blendScreen,
  float,
  mod,
  mx_cell_noise_float,
  oneMinus,
  smoothstep,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  pass,
  mix,
  add,
} from 'three/tsl';

const TEXTUREMAP_SRC = 'https://i.postimg.cc/XYwvXN8D/img-4.png';
const DEPTHMAP_SRC = 'https://i.postimg.cc/2SHKQh2q/raw-4.webp';

const PLANE_W = 300;
const PLANE_H = 300;
const SCALE = 0.40;

// ── Three.js scene (runs outside React) ─────────────────────────────

const ThreeCanvas = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let animId = 0;

    (async () => {
      // --- renderer ---
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block';
      container.appendChild(canvas);

      const renderer = new THREE.WebGPURenderer({ canvas, antialias: true } as any);
      await renderer.init();
      if (disposed) { renderer.dispose(); canvas.remove(); return; }

      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      // --- scene & camera ---
      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
      camera.position.z = 1;

      // --- resize helper ---
      const resize = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        renderer.setSize(w, h);
        const aspect = w / h;
        camera.left = -aspect;
        camera.right = aspect;
        camera.top = 1;
        camera.bottom = -1;
        camera.updateProjectionMatrix();
      };
      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(container);

      // --- textures ---
      const loader = new THREE.TextureLoader();
      const [rawMap, depthMap] = await Promise.all([
        loader.loadAsync(TEXTUREMAP_SRC),
        loader.loadAsync(DEPTHMAP_SRC),
      ]);
      if (disposed) { renderer.dispose(); canvas.remove(); ro.disconnect(); return; }

      // --- material (TSL nodes – identical to original) ---
      const uPointer = uniform(new THREE.Vector2(0));
      const uProgress = uniform(0);

      const tDepthMap = texture(depthMap);
      const tMap = texture(rawMap, uv().add(tDepthMap.r.mul(uPointer).mul(0.01)));

      const aspect = float(PLANE_W).div(PLANE_H);
      const tUv = vec2(uv().x.mul(aspect), uv().y);
      const tiling = vec2(120.0);
      const tiledUv = mod(tUv.mul(tiling), 2.0).sub(1.0);
      const brightness = mx_cell_noise_float(tUv.mul(tiling).div(2));
      const dist = float(tiledUv.length());
      const dot = float(smoothstep(0.5, 0.49, dist)).mul(brightness);
      const depth = tDepthMap;
      const flow = oneMinus(smoothstep(0, 0.02, abs(depth.sub(uProgress))));
      const mask = dot.mul(flow).mul(vec3(10, 0, 0));
      const final = blendScreen(tMap, mask);

      const material = new THREE.MeshBasicNodeMaterial({
        colorNode: final,
        transparent: true,
        opacity: 0,
      });

      // --- mesh ---
      const geo = new THREE.PlaneGeometry(1, 1);
      const meshAspect = PLANE_W / PLANE_H;
      const scaleX = meshAspect * SCALE * 2;
      const scaleY = SCALE * 2;
      const mesh = new THREE.Mesh(geo, material);
      mesh.scale.set(scaleX, scaleY, 1);
      scene.add(mesh);

      // --- post-processing ---
      const postProcessing = new THREE.PostProcessing(renderer as any);
      const scenePass = pass(scene, camera);
      const scenePassColor = scenePass.getTextureNode('output');
      const bloomPass = bloom(scenePassColor, 1, 0.5, 1);

      const uScanProgress = uniform(0);
      const scanPos = float(uScanProgress.value);
      const uvY = uv().y;
      const scanWidth = float(0.05);
      const scanLine = smoothstep(0, scanWidth, abs(uvY.sub(scanPos)));
      const redOverlay = vec3(1, 0, 0).mul(oneMinus(scanLine)).mul(0.4);
      const withScanEffect = mix(
        scenePassColor,
        add(scenePassColor, redOverlay),
        smoothstep(0.9, 1.0, oneMinus(scanLine)),
      );
      postProcessing.outputNode = withScanEffect.add(bloomPass);

      // --- pointer ---
      const pointer = new THREE.Vector2(0, 0);
      const onPointerMove = (e: PointerEvent) => {
        pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
        pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
      };
      window.addEventListener('pointermove', onPointerMove);

      // --- clock ---
      const clock = new THREE.Clock();
      let currentOpacity = 0;

      const animate = () => {
        if (disposed) return;
        animId = requestAnimationFrame(animate);

        const t = clock.getElapsedTime();
        const prog = Math.sin(t * 0.5) * 0.5 + 0.5;
        uProgress.value = prog;
        uScanProgress.value = prog;
        uPointer.value = pointer;

        // fade-in
        currentOpacity = THREE.MathUtils.lerp(currentOpacity, 1, 0.07);
        (material as any).opacity = currentOpacity;

        postProcessing.renderAsync();
      };
      animate();

      // --- cleanup (captured by disposed flag) ---
      return () => {
        disposed = true;
        cancelAnimationFrame(animId);
        window.removeEventListener('pointermove', onPointerMove);
        ro.disconnect();
        renderer.dispose();
        geo.dispose();
        material.dispose();
        rawMap.dispose();
        depthMap.dispose();
        canvas.remove();
      };
    })().then((cleanup) => {
      // Store cleanup so the outer effect can call it
      if (cleanup) (container as any).__threeCleanup = cleanup;
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(animId);
      const cleanup = (container as any).__threeCleanup;
      if (cleanup) cleanup();
    };
  }, []);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
};

// ── React overlay + canvas ──────────────────────────────────────────

export const Html = () => {
  // Bevisst full sidelast (ikke SPA-navigate) for å unngå at WebGPU-renderens
  // cleanup-race mot React-unmount blokkerer neste sides rendering.
  const titleWords = 'Scenechat'.split(' ');
  const subtitle = 'La publikum forme showet';
  const [visibleWords, setVisibleWords] = useState(0);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [delays, setDelays] = useState<number[]>([]);
  const [subtitleDelay, setSubtitleDelay] = useState(0);

  useEffect(() => {
    setDelays(titleWords.map(() => Math.random() * 0.07));
    setSubtitleDelay(Math.random() * 0.1);
  }, [titleWords.length]);

  useEffect(() => {
    if (visibleWords < titleWords.length) {
      const timeout = setTimeout(() => setVisibleWords(visibleWords + 1), 600);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => setSubtitleVisible(true), 800);
      return () => clearTimeout(timeout);
    }
  }, [visibleWords, titleWords.length]);

  return (
    <div className="h-svh" style={{ position: 'relative', overflow: 'hidden', background: '#000' }}>
      {/* 3D canvas */}
      <ThreeCanvas />

      {/* text overlay */}
      <div className="h-svh uppercase items-center w-full absolute z-[60] pointer-events-none px-10 flex justify-start pt-[22vh] flex-col">
        <div className="text-3xl md:text-5xl xl:text-6xl 2xl:text-7xl font-extrabold">
          <div className="flex space-x-2 lg:space-x-6 overflow-hidden text-white">
            {titleWords.map((word, index) => (
              <div
                key={index}
                className={index < visibleWords ? 'fade-in' : ''}
                style={{
                  animationDelay: `${index * 0.13 + (delays[index] || 0)}s`,
                  opacity: index < visibleWords ? undefined : 0,
                }}
              >
                {word}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs md:text-xl xl:text-2xl 2xl:text-3xl mt-2 overflow-hidden text-white font-bold">
          <div
            className={subtitleVisible ? 'fade-in-subtitle' : ''}
            style={{
              animationDelay: `${titleWords.length * 0.13 + 0.2 + subtitleDelay}s`,
              opacity: subtitleVisible ? undefined : 0,
            }}
          >
            {subtitle}
          </div>
        </div>
      </div>

      <button
        className="explore-btn"
        style={{ animationDelay: '2.2s' }}
        onClick={() => { window.location.href = '/signin'; }}
      >
        Logg inn
        <span className="explore-arrow">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5V17" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 12L11 17L16 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      </button>
    </div>
  );
};

export default Html;
